import { Permission } from '@vitesse/proto/vitesse/v1/entitlements_pb.js';
/*
 * PLATFORM TOOLS, declared once, implemented by whichever app is open.
 *
 * "What is on screen?" is not the AI Console's question, or the Fleet Manager's. It is the same
 * question everywhere, with the same arguments and the same meaning, and only the ANSWER differs by
 * app. Declaring it per-UI would give an administrator three rows to govern for one capability, in
 * three groups, each of which could be set differently by accident. It would also give the model
 * three names for one thing, which is how a model ends up calling the wrong one.
 *
 * So the DECLARATION is central and the IMPLEMENTATION is per-app. A UI calls
 * `implementPlatformTool('platform.whatIsOnScreen', fn)`; the declaration it is offered under comes
 * from here, identical in every app.
 *
 * A TOOL NOBODY IMPLEMENTS IS NOT OFFERED. This is the whole reason the two halves are separate: the
 * catalog can name a capability the platform has without every app having to have it, and a model in
 * an app that cannot read a selection is never told it can. Declaring it anyway and failing at call
 * time would spend a turn to learn what the offer already knew.
 *
 * THE `platform.` PREFIX IS LOAD-BEARING. It is how the worker knows a tool's origin is the platform
 * rather than the app that happened to announce it, which is what puts it in one governance group
 * instead of three. Same mechanism as `mcp__<server>__`.
 */
import { registerClientTools } from './tools';
import { readScreen, readSelection } from './screen';
/** The prefix that marks a tool as the platform's rather than one app's. */
export const PLATFORM_PREFIX = 'platform.';
/*
 * THE SET.
 *
 * Deliberately small, and deliberately about the USER'S CONTEXT rather than about data. Anything the
 * AI can fetch from the server belongs on the server, where it works with no tab open; these are the
 * things only the browser knows, which is the entire justification for a client tool existing.
 *
 * Descriptions are written for the MODEL, not for a person. They say when to reach for the tool,
 * because that is the decision the model is actually making, and a description that only restates
 * the name gives it nothing to decide with.
 */
/*
 * The three of these need NO permission, and that is a decision rather than an
 * omission: reading which page somebody is on, what they have selected, and
 * moving them to another page are all things the person could do by looking and
 * clicking. Gating them would deny an agent what the person driving it can
 * already see, which is not a security boundary — it is an agent that cannot
 * answer "what is this".
 */
export const PLATFORM_TOOLS = [
    {
        name: 'platform.whatIsOnScreen',
        description: 'Read what the user is currently looking at: which page, which entity, and what is visible on it. ' +
            'Use it ONLY for a deictic reference — "this", "here", "what am I looking at" — where the user is pointing ' +
            'at their own screen rather than naming something. It is NOT a search or listing tool: it returns a snapshot ' +
            'of one viewport, not the plant. To find a relay, controller, symbol, model, or dashboard, use that ' +
            "domain's own search/list tool (e.g. symbols.search, ai_admin_list_dashboards) — it will search the whole " +
            'scope, not just what happens to be rendered right now.',
        parameters: { type: 'object', properties: {} },
    },
    {
        name: 'platform.readSelection',
        description: 'Read whatever the user has selected or highlighted right now, if anything. Use it when they refer to ' +
            'a selection ("explain this", "what does that mean") rather than naming what they mean.',
        parameters: { type: 'object', properties: {} },
    },
    {
        name: 'platform.navigate',
        description: 'Move the user to a place in the app by its path, the same identifier the address bar uses. Use it to take ' +
            'them somewhere you are talking about, rather than describing how to click their way there. NOT needed to ' +
            'reach a relay for your own reads — every relay-scoped tool takes a `gateway` parameter and works on any ' +
            "relay the org owns, whether or not it's open on screen.",
        parameters: {
            type: 'object',
            properties: { path: { type: 'string', description: 'Where to go, e.g. /usage or /config.' } },
            required: ['path'],
        },
    },
];
const byName = new Map(PLATFORM_TOOLS.map((t) => [t.name, t]));
/**
 * Supply this app's answer to one platform capability.
 *
 * Returns an unregister function, like `registerClientTools`, so a view that implements one while it
 * is mounted can withdraw it on the way out. An unknown name is IGNORED rather than thrown on: an
 * app pinned to an older base-ui implementing a tool this version has since renamed should lose that
 * capability, not fail to start.
 */
export function implementPlatformTool(name, run) {
    const spec = byName.get(name);
    if (!spec)
        return () => undefined;
    const tool = { permission: Permission.UNSPECIFIED, ...spec, run: async (args) => run(args) };
    return registerClientTools(tool);
}
/** Is this one of ours? Used by the worker's mirror of the same rule, and by anything routing on it. */
export const isPlatformTool = (name) => name.startsWith(PLATFORM_PREFIX);
/**
 * The default implementations, which are the SAME in every app because they are pure DOM.
 *
 * `whatIsOnScreen` and `readSelection` do not need to know anything about a particular UI: they read
 * what is rendered. Providing them centrally means an app gets them by calling one function rather
 * than by reimplementing a screen reader three times and having the three drift.
 *
 * `navigate` is NOT here. Every app routes differently, and a tool that guessed would send the user
 * somewhere that does not exist; an app supplies it with `implementPlatformTool` or the model is
 * simply never offered it, which is the honest outcome.
 *
 * Returns an unregister function covering all of them, so a host that tears down cleanly can.
 */
export function implementDomPlatformTools() {
    const offs = [
        implementPlatformTool('platform.whatIsOnScreen', () => {
            const view = readScreen();
            return {
                ok: true,
                // The MESSAGE is what the model reads; the data is what it can quote precisely. Prose first,
                // because a model handed only JSON tends to describe the JSON.
                message: `The user is on "${view.title}" at ${view.path}, and this is what is in their VIEWPORT — ` +
                    'what they can see right now, not the whole page. ' +
                    (view.rows ? ` A list is showing ${view.rows.count} rows${view.rows.total ? ` of ${view.rows.total}` : ''}.` : '') +
                    (view.truncated ? ' There is more further down or off-screen; scroll or use a data tool for the rest.' : ''),
                data: view,
            };
        }),
        implementPlatformTool('platform.readSelection', () => {
            const text = readSelection();
            return text
                ? { ok: true, message: `The user has selected: ${text}`, data: { selection: text } }
                : // NOT an error. Nothing selected is a true and useful answer, and reporting it as a failure
                    // would make the model retry a tool whose answer will not change.
                    { ok: true, message: 'Nothing is selected right now. Ask the user what they mean, or read the screen.' };
        }),
    ];
    return () => offs.forEach((off) => off());
}
//# sourceMappingURL=platform-tools.js.map